import { type FC, useMemo } from "react";
import { Button, DialogContent } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FormBuilder } from "@/shared/components/Form";
import { DialogHeader } from "@/shared/components";
import { useLiveEnabled } from "@/partner/entities/instance";
import { linkTargetInput } from "@/partner/features/links";
import { type PlatformMenuItemFormValues } from "../model";
import { makePlatformMenuItemFormSchema } from "../model/schemas";

/** Состояние пункта меню, от которого зависят подписи кнопок формы. */
type MenuItemFormState = "draft" | "published";

const PRIMARY_ACTION_LABEL_KEYS: Record<MenuItemFormState, string> = {
  draft: "common.actions.publish",
  published: "common.actions.save",
};

const SECONDARY_ACTION_LABEL_KEYS: Record<MenuItemFormState, string> = {
  draft: "common.actions.save",
  published: "common.actions.hide",
};

interface MenuItemFormProps {
  title: string;
  loading?: boolean;
  initialValues?: PlatformMenuItemFormValues;
  onSubmit: (values: PlatformMenuItemFormValues) => Promise<void>;
  onClose: () => void;
}

export const MenuItemForm: FC<MenuItemFormProps> = ({
  title,
  loading = false,
  initialValues = {
    linkType: "INTERNAL",
  },
  onSubmit,
  onClose,
}) => {
  const { t } = useTranslation();
  const liveEnabled = useLiveEnabled();
  const schema = useMemo(
    () => makePlatformMenuItemFormSchema(t, liveEnabled),
    [t, liveEnabled]
  );

  const isDraft = !initialValues?.publishAt;
  const state: MenuItemFormState = isDraft ? "draft" : "published";
  let publish = !isDraft;

  const handleSubmit = async (values: Record<string, any>) => {
    onSubmit({
      publishAt: publish ? new Date().toISOString() : null,
      label: values.label,
      linkType: values.linkType,
      href: values.href,
      ...linkTargetInput(values, values.linkType === "INTERNAL"),
    });
  };

  return (
    <>
      <DialogHeader
        title={title}
        actions={[
          <Button
            variant="contained"
            type="submit"
            form="platform-menu-item-form"
            disabled={loading}
            onClick={() => (publish = true)}
          >
            {t(PRIMARY_ACTION_LABEL_KEYS[state])}
          </Button>,
          <Button
            variant="outlined"
            type="submit"
            form="platform-menu-item-form"
            disabled={loading}
            onClick={() => (publish = false)}
          >
            {t(SECONDARY_ACTION_LABEL_KEYS[state])}
          </Button>,
        ]}
        loading={loading}
        onClose={onClose}
      />

      <DialogContent sx={{ p: 1 }}>
        <FormBuilder
          formId="platform-menu-item-form"
          schema={schema}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          disabled={loading}
          submitButton={false}
          resetButton={false}
          paper
        />
      </DialogContent>
    </>
  );
};
